import React, { Fragment, useState, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';

import TintForm from './tint';

const {
  apiFetch,
  i18n: {
    __,
  },
} = wp;

/* eslint-disable max-len */

/**
 * UGCUpload component.
 */
const UGCUpload = (props) => {
  const {
    tintboard,
    filestackapikey,
    partition,
    success,
    vendor,
  } = props;

  const useTint = 0 < filestackapikey.length && 0 < tintboard.length;

  const {
    location: {
      href = '',
    } = {},
  } = window || {};

  if (href.includes('message=success')) {
    return (
      <p>
        {__('Thanks for submitting your media.', 'nbc')}
      </p>
    );
  }

  const [categories, setCategories] = useState([]);
  const inputRef = useRef(null);
  const errorDisplay = useRef(null);
  useEffect(() => {
    async function fetchData() {
      const result = await apiFetch({
        path: `/nbc/v1/ugc-categories?partition=${partition}`,
      });
      setCategories(result);
    }
    fetchData();
  }, [partition]);

  const actionUrl = useTint ? '' : 'https://api.celljournalist.com/Media/VendorUpload';

  const [tags, setTags] = useState('');
  const [category, setCategory] = useState('');
  const [mediaURL, setMediaURL] = useState('');
  const [description, setDescription] = useState('');
  const [name, setName] = useState('');
  const [title, setTitle] = useState('');
  const [email, setEmail] = useState('');
  const [agreedTOS, setAgreedTOS] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errorSubmitting, setErrorSubmitting] = useState(false);

  const hasEmptyFields = (
    ! mediaURL
    || ! description
    || ! name
    || ! title
    || ! email
    || ! agreedTOS
  );

  const submitDisabled = useTint && (hasEmptyFields || isSubmitting);

  const logEvent = (config) => {
    const {
      nbc: {
        callLetters = '',
      },
    } = window || {};

    let streamName = 'ugc-form-submissions';

    if (callLetters) {
      streamName = `${callLetters}-${streamName}`;
    }

    const {
      message = '',
      context = [],
    } = config || {};

    const {
      level = 'info',
    } = config || {};

    const body = {
      activity: 'UGC Form Submit',
      context,
      group: 'ugc-logs',
      level,
      message,
      stream: streamName,
    };

    return apiFetch({
      path: '/nbc-cloudwatch-logs/v1/log',
      method: 'POST',
      headers: {
        'content-type': 'application/json',
      },
      body: JSON.stringify(body),
    });
  };

  const handleSubmit = async (evt) => {
    if (! useTint) {
      // if we're not using Tint, we allow a standard form submit.
      return;
    }

    // if we are using Tint, we should prevent default form submission action.
    evt.preventDefault();

    // stop the user from pressing submit multiple times.
    setIsSubmitting(true);
    setErrorSubmitting(false);

    const {
      nbc: {
        siteID = null,
      } = {},
    } = window || {};

    // set the POST data.
    const body = {
      data: {
        type: 'post',
        attributes: {
          text: description,
          tags: [],
          media_url: [
            mediaURL,
          ],
          author: {
            name,
          },
          custom_field: JSON.stringify({
            title,
            email,
            agreedTOS,
            orginatingSiteID: siteID,
            tags: tags.split(',').map((tag) => tag.trim()),
          }),
        },
      },
    };

    if (category) {
      body.data.attributes.tags = [category];
    }

    // Submit using the Tint Public Post API.
    // @see: https://developers.tintup.com/v2/#tag/posts/paths/~1v2~1tints~1{tint_slug}~1posts/post .

    let response = null;
    let error = null;

    try {
      response = await fetch(`https://api.tintup.com/v2/tints/${tintboard}/posts`, {
        method: 'POST',
        headers: {
          'content-type': 'application/vnd.api+json',
        },
        body: JSON.stringify(body),
      });
    } catch {
      error = 'Failed to submit to Tint.';
      await logEvent({ level: 'error', message: error });
      setIsSubmitting(false);
      setErrorSubmitting(true);
      throw new Error(error);
    }

    if (! (response || {}).ok) {
      error = `HTTP error! status: ${response.status}`;
      await logEvent({ level: 'error', message: error });
      throw new Error(error);
    }

    try {
      await response.json();
    } catch {
      error = 'Invalid response from Tint!';
      await logEvent({ level: 'error', message: error });
      setIsSubmitting(false);
      setErrorSubmitting(true);
    }

    await logEvent({ message: 'Submitted to Tint', context: body });
    window.location.href = `${href}?message=success`; // phpcs:ignore WordPressVIPMinimum.JS.Window.location
  };

  return (
    <Fragment key="ugcForm">
      <form
        action={actionUrl}
        encType="multipart/form-data"
        method="post"
        onSubmit={async (evt) => handleSubmit(evt)}
      >
        <div className="nf-form-fields-required">
          <span className="ninja-forms-req-symbol">*</span>
          {__('Required Fields', 'nbc')}
        </div>
        <fieldset>
          {useTint
            ? (
              <Fragment>
                <TintForm
                  filestackApiKey={filestackapikey}
                  onFileUploaded={(file) => setMediaURL(file.url)}
                />
                <label htmlFor="datafile">
                  {__('Choose a file to upload.', 'nbc')} <span className="ninja-forms-req-symbol">*</span>
                </label>
              </Fragment>
            )
            : (
              <Fragment>
                <div className="ugc-error-hidden" ref={errorDisplay}><span>{__('Images must be at least 800 x 800 pixels.', 'nbc')}</span></div>
                <input
                  id="datafile"
                  name="datafile"
                  onChange={(e) => {
                    const fileURL = window.URL || window.webkitURL;
                    // eslint-disable-next-line prefer-destructuring
                    const file = e.target.files[0];
                    let img;
                    if (file) {
                      img = new Image();
                      img.onload = () => {
                        if (800 > img.width || 800 > img.height) {
                          // Image dimensions are too small.
                          // eslint-disable-next-line no-console
                          console.log('image too small');
                          inputRef.current.value = null;
                          errorDisplay.current.className = 'ugc-error-visible';
                        } else {
                          errorDisplay.current.className = 'ugc-error-hidden';
                        }
                      };
                      img.onerror = () => {
                        // eslint-disable-next-line no-console, prefer-template
                        console.log('not an image file: ' + file.type);
                      };
                      img.src = fileURL.createObjectURL(file);
                    }
                  }}
                  ref={inputRef}
                  required="required"
                  size="40"
                  type="file"
                />
                <label htmlFor="datafile">
                  {__('Choose a file to upload. Images must be at least 800 x 800 pixels.', 'nbc')} <span className="ninja-forms-req-symbol">*</span>
                </label>
              </Fragment>
            )
          }
        </fieldset>
        <label htmlFor="title">
          {__('Title', 'nbc')}
          <span className="ninja-forms-req-symbol">*</span>
        </label>
        <input
          id="title"
          name="title"
          placeholder={__('Use a short descriptive title', 'nbc')}
          required="required"
          size="30"
          type="text"
          onChange={(evt) => setTitle(evt.target.value)}
        />
        <label htmlFor="description">
          {__('Description', 'nbc')}
          <span className="ninja-forms-req-symbol">*</span>
        </label>
        <textarea
          cols="75"
          name="description"
          placeholder={__('Please type in a description', 'nbc')}
          rows="4"
          onChange={(e) => setDescription(e.target.value)}
        />
        {
          0 < categories.length &&
          <fieldset>
            {/* Select not added to label rule in current version of eslint. */}
            {/* eslint-disable jsx-a11y/label-has-for */}
            <label htmlFor="channelId">
              {__('Select a Category', 'nbc')}
            </label>
            <select
              name="channelId"
              id="channelId"
              onChange={(e) => setCategory(e.target.value)}
            >
              <option aria-label="category" />
              {categories.map((channel) => (
                <option
                  key={channel.id}
                  value={channel.id}
                  title={channel.tags.join(', ')}
                >
                  {channel.name}
                </option>
              ))}
            </select>
            {/* eslint-enable jsx-a11y/label-has-for */}
            <label htmlFor="tags">
              {__('Tags', 'nbc')}
              <span className="ninja-forms-req-symbol">*</span>
            </label>
            <input
              id="tags"
              name="tags"
              placeholder={
                __('comma separated - ex: hurricane, weather', 'nbc')
              }
              onChange={(e) => setTags(e.target.value)}
              required="required"
              size="30"
              type="text"
            />
          </fieldset>
        }
        <fieldset>
          <label htmlFor="credit">
            {__('Your Name', 'nbc')}
            <span className="ninja-forms-req-symbol">*</span>
          </label>
          <input
            name="credit"
            placeholder={__('First & last name', 'nbc')}
            size="30"
            type="text"
            required="required"
            onChange={(evt) => setName(evt.target.value)}
          />
          <label htmlFor="email">
            {__('Email', 'nbc')}
            <span className="ninja-forms-req-symbol">*</span>
          </label>
          <input
            name="email"
            placeholder={__('ex: mailAddress@client.com', 'nbc')}
            size="30"
            type="email"
            required="required"
            onChange={(evt) => setEmail(evt.target.value)}
          />
        </fieldset>
        <label htmlFor="terms" style={{ marginBottom: '1.5625rem' }}>
          <input
            name="terms"
            type="checkbox"
            required="required"
            onChange={(evt) => setAgreedTOS(evt.target.checked)}
          />
          {__('I agree to the ', 'nbc')}
          <a href="/terms">
            {__('terms of service', 'nbc')}
          </a> {__(' and ', 'nbc')}
          <a href="//www.nbcuniversal.com/privacy/">
            {__('privacy policy', 'nbc')}
          </a>.<span className="ninja-forms-req-symbol">*</span>
        </label>
        <hr />
        <input
          type="hidden"
          name="vendorId"
          value={vendor}
        />
        <input
          type="hidden"
          name="partitionId"
          value={partition}
        />
        <input
          type="hidden"
          name="filename"
        />
        <input
          type="hidden"
          name="successUrl"
          value={success}
        />
        <fieldset>
          <input type="submit" value={__('Submit Your Media', 'nbc')} disabled={submitDisabled} />
          {! (useTint && errorSubmitting) ? '' : (
            <div className="ugc-error-visible"><span>{__('An unexpected submission error occurred. Please try again.', 'nbc')}</span></div>
          )}
        </fieldset>
      </form>
    </Fragment>
  );
};

export default UGCUpload;

UGCUpload.defaultProps = {
  partition: '',
  filestackapikey: '',
  tintboard: '',
  success: '',
  vendor: '',
};

UGCUpload.propTypes = {
  partition: PropTypes.string,
  filestackapikey: PropTypes.string,
  tintboard: PropTypes.string,
  success: PropTypes.string,
  vendor: PropTypes.string,
};
